import { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useDropdowns from 'src/hooks/useDropdowns'
import { AddUnit } from 'src/redux/actions'
import {
  buildSampleRows,
  CONTENT_PATH_DEPTH,
  importRows,
  parseNumber,
  parseStatus,
  parseTimeUnit,
  parseVisibility,
  requireValues,
  resolveContentPath,
  resolveOptionKey,
} from 'src/util/bulkImport'
import { downloadCsv } from 'src/util/csv'

import BulkUploadPanel from '../helperComponents/BulkUploadPanel'

export const COURSE_CSV_HEADERS = [
  'name',
  'description',
  'language',
  'difficultyLevel',
  'hrs',
  'minutes',
  'sortOrder',
  'status',
  'visibility',
  'videoUrl',
  'videoThumbnailUrl',
  'courseDetailsThumbnailUrl',
]

const SAMPLE_COURSES = [
  {
    name: 'Basics of Greetings',
    description: 'Learn common greetings and introductions used in everyday conversation',
    language: 'English',
    difficultyLevel: 'Beginner',
    hrs: '00',
    minutes: '15',
    sortOrder: '1',
    status: 'Active',
    visibility: 'Students',
    videoUrl: 'https://example.com/media/greetings_intro.mp4',
    videoThumbnailUrl: 'https://example.com/media/greetings_thumb.jpg',
    courseDetailsThumbnailUrl: 'https://example.com/media/greetings_details.jpg',
  },
  {
    name: 'Numbers and Counting',
    description: 'Master numbers 1-100 and basic counting exercises',
    language: 'English',
    difficultyLevel: 'Absolute Beginners',
    hrs: '00',
    minutes: '20',
    sortOrder: '2',
    status: 'Active',
    visibility: 'Students',
    videoUrl: 'https://example.com/media/numbers_intro.mp4',
    videoThumbnailUrl: 'https://example.com/media/numbers_thumb.jpg',
    courseDetailsThumbnailUrl: 'https://example.com/media/numbers_details.jpg',
  },
  {
    name: 'Business Communication',
    description: 'Professional vocabulary and email/phone etiquette for the workplace',
    language: 'Spanish',
    difficultyLevel: 'Intermidiate',
    hrs: '01',
    minutes: '00',
    sortOrder: '3',
    status: 'Inactive',
    visibility: 'Students',
    videoUrl: '',
    videoThumbnailUrl: '',
    courseDetailsThumbnailUrl: '',
  },
  {
    name: 'Internal Review Draft',
    description: 'Draft course visible only to admins while content is being reviewed',
    language: 'English',
    difficultyLevel: 'Interesting Stuff',
    hrs: '00',
    minutes: '10',
    sortOrder: '4',
    status: 'Active',
    visibility: 'AdminOnly',
    videoUrl: '',
    videoThumbnailUrl: '',
    courseDetailsThumbnailUrl: '',
  },
]

export const SAMPLE_COURSE_CSV_ROWS = buildSampleRows(COURSE_CSV_HEADERS, SAMPLE_COURSES)

export const downloadSampleCourseCsv = () => downloadCsv('bulk-courses-sample.csv', SAMPLE_COURSE_CSV_ROWS)

// Mirrors the payload AddEditCourse posts, with media passed as direct URLs
// instead of file uploads.
export const buildCoursePayload = (row, { dropdowns, difficultyLevels }) => {
  requireValues(row, ['name', 'description'])

  return {
    ...resolveContentPath(dropdowns, row, CONTENT_PATH_DEPTH.LANGUAGE),
    name: row.name,
    description: row.description,
    difficultyLevel: resolveOptionKey(difficultyLevels, row.difficultyLevel, 'difficultyLevel'),
    hrs: parseTimeUnit(row.hrs, 'hrs', 50),
    minutes: parseTimeUnit(row.minutes, 'minutes', 59),
    sortOrder: parseNumber(row.sortOrder, 'sortOrder'),
    isActive: parseStatus(row.status),
    unPublished: parseVisibility(row.visibility),
    video: row.videoUrl,
    videoThumbnail: row.videoThumbnailUrl,
    courseDetailsThumbnail: row.courseDetailsThumbnailUrl,
  }
}

const BulkUploadCourse = ({ toggleDrawer, refetch }) => {
  const dispatch = useDispatch()
  const { dropdowns } = useDropdowns()
  const difficultyLevels = useSelector(store => store.mainReducer.courseDifficultyLevel)

  const onImport = useCallback(
    rows =>
      importRows(rows, async row => {
        await dispatch(AddUnit(buildCoursePayload(row, { dropdowns, difficultyLevels })))
      }),
    [dispatch, dropdowns, difficultyLevels]
  )

  return (
    <BulkUploadPanel
      entityPlural="Courses"
      headers={COURSE_CSV_HEADERS}
      onDownloadSample={downloadSampleCourseCsv}
      onImport={onImport}
      toggleDrawer={toggleDrawer}
      refetch={refetch}
      helpText={
        'language must match an existing Language by name or id (e.g. "English"). ' +
        'difficultyLevel accepts either label or key: ' +
        `${(difficultyLevels || []).map(level => level.value).join(', ')}. ` +
        'status is Active/Inactive, visibility is Students/AdminOnly. ' +
        'The three media columns take direct URLs and may be left blank.'
      }
    />
  )
}

export default BulkUploadCourse
